import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store de notificaciones en tiempo real. Recibe eventos del hook
 * useTicketEventsStream (SSE), mantiene un contador no leído para el badge
 * del sidebar y la lista de las últimas notificaciones.
 *
 * Persiste `soundEnabled` y `browserNotifEnabled` en localStorage para que
 * cada usuario tenga su preferencia entre sesiones. NO persiste
 * `unreadCount` ni `notifications` — al cerrar el panel y reabrirlo, el
 * badge arranca en 0 (mark-as-read implícito).
 */

export interface TicketEvent {
  type: string;
  ticket_id: number;
  ticket_code?: string;
  ticket_subject?: string;
  actor_type: 'agent' | 'customer' | 'system';
  actor_id: number | null;
  payload: unknown;
  created_at: string;
}

interface NotificationsState {
  // Estado en memoria (no persiste).
  unreadCount: number;
  notifications: TicketEvent[];
  connected: boolean;

  // Preferencias del usuario (persisten).
  soundEnabled: boolean;
  browserNotifEnabled: boolean;

  // Actions.
  addEvent: (e: TicketEvent) => void;
  markAllRead: () => void;
  setConnected: (v: boolean) => void;
  setSoundEnabled: (v: boolean) => void;
  setBrowserNotifEnabled: (v: boolean) => void;
}

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set) => ({
      unreadCount: 0,
      notifications: [],
      connected: false,
      soundEnabled: false,
      browserNotifEnabled: false,

      addEvent: (e) =>
        set((state) => ({
          unreadCount: state.unreadCount + 1,
          // Mantenemos las últimas 50 — más que eso es ruido.
          notifications: [e, ...state.notifications].slice(0, 50),
        })),

      markAllRead: () => set({ unreadCount: 0 }),

      setConnected: (connected) => set({ connected }),
      setSoundEnabled: (soundEnabled) => set({ soundEnabled }),
      setBrowserNotifEnabled: (browserNotifEnabled) => set({ browserNotifEnabled }),
    }),
    {
      name: 'vipsoft:notifications',
      // Solo persistimos las preferencias.
      partialize: (state) => ({
        soundEnabled: state.soundEnabled,
        browserNotifEnabled: state.browserNotifEnabled,
      }),
    },
  ),
);
